/**
 * Library Page
 * 
 * A curated collection of book recommendations and downloadable resources
 * from Crystal Khalil and Dr. Nicole LaBeach for Inner Circle members.
 * Content is managed by admins via the Admin Dashboard.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { 
  BookOpen, Download, ExternalLink, Star, Heart, 
  Sparkles, Target, Users, Brain, Flame, Shield
} from 'lucide-react';

const API_URL = window.location.origin;

// Icon mapping for dynamic icons from backend
const ICON_MAP = {
  Heart, Star, Sparkles, Target, Users, Brain, Flame, Shield, BookOpen, Download
};

// Default book recommendations (used if database is empty)
// Crystal & Dr. Nicole's favorites
const DEFAULT_BOOKS = [
  // ===== BRENÉ BROWN COLLECTION =====
  { id: "default-1", title: "The Gifts of Imperfection", author: "Brené Brown", description: "Let go of who you think you're supposed to be and embrace who you are. A guide to wholehearted living.", category: "Self-Worth", amazon_link: "https://www.amazon.com/Gifts-Imperfection-Think-Supposed-Embrace/dp/159285849X", icon: "Heart", recommended_by: "Crystal & Dr. Nicole" },
  { id: "default-2", title: "Daring Greatly", author: "Brené Brown", description: "How the courage to be vulnerable transforms the way we live, love, parent, and lead.", category: "Leadership", amazon_link: "https://www.amazon.com/Daring-Greatly-Courage-Vulnerable-Transforms/dp/1592408419", icon: "Flame", recommended_by: "Dr. Nicole" },
  { id: "default-12", title: "Rising Strong", author: "Brené Brown", description: "The physics of vulnerability - how to get back up after falling.", category: "Resilience", amazon_link: "https://www.amazon.com/Rising-Strong-Ability-Transforms-Parent/dp/0812985818", icon: "Flame", recommended_by: "Dr. Nicole" },
  { id: "default-13", title: "Atlas of the Heart", author: "Brené Brown", description: "Mapping meaningful connection and the language of human experience.", category: "Self-Discovery", amazon_link: "https://www.amazon.com/Atlas-Heart-Meaningful-Connection-Experience/dp/0399592555", icon: "Heart", recommended_by: "Crystal & Dr. Nicole" },
  
  // ===== BOUNDARIES & HEALING =====
  { id: "default-3", title: "Set Boundaries, Find Peace", author: "Nedra Glover Tawwab", description: "A guide to reclaiming yourself through healthy boundaries in relationships and work.", category: "Boundaries", amazon_link: "https://www.amazon.com/Set-Boundaries-Find-Peace-Reclaiming/dp/0593192095", icon: "Shield", recommended_by: "Crystal" },
  { id: "default-4", title: "The Body Keeps the Score", author: "Bessel van der Kolk", description: "Understanding trauma and how it affects the body, mind, and spirit - and paths to healing.", category: "Healing", amazon_link: "https://www.amazon.com/Body-Keeps-Score-Healing-Trauma/dp/0143127748", icon: "Brain", recommended_by: "Dr. Nicole" },
  { id: "default-14", title: "Boundaries", author: "Dr. Henry Cloud & Dr. John Townsend", description: "When to say yes, how to say no to take control of your life.", category: "Boundaries", amazon_link: "https://www.amazon.com/Boundaries-Updated-Expanded-When-Control/dp/0310351804", icon: "Shield", recommended_by: "Crystal" },
  
  // ===== PERSONAL GROWTH =====
  { id: "default-5", title: "Atomic Habits", author: "James Clear", description: "An easy and proven way to build good habits and break bad ones. Small changes, remarkable results.", category: "Growth", amazon_link: "https://www.amazon.com/Atomic-Habits-Proven-Build-Break/dp/0735211299", icon: "Target", recommended_by: "Crystal" },
  { id: "default-6", title: "The Four Agreements", author: "Don Miguel Ruiz", description: "A practical guide to personal freedom based on ancient Toltec wisdom.", category: "Self-Worth", amazon_link: "https://www.amazon.com/Four-Agreements-Practical-Personal-Freedom/dp/1878424319", icon: "Sparkles", recommended_by: "Crystal & Dr. Nicole" },
  { id: "default-15", title: "You Are a Badass", author: "Jen Sincero", description: "How to stop doubting your greatness and start living an awesome life.", category: "Growth", amazon_link: "https://www.amazon.com/You-Are-Badass-Doubting-Greatness/dp/0762447699", icon: "Star", recommended_by: "Crystal" },
  { id: "default-16", title: "Think Like a Monk", author: "Jay Shetty", description: "Train your mind for peace and purpose every day.", category: "Spirituality", amazon_link: "https://www.amazon.com/Think-Like-Monk-Train-Purpose/dp/1982134488", icon: "Brain", recommended_by: "Dr. Nicole" },
  
  // ===== SELF-DISCOVERY & EMPOWERMENT =====
  { id: "default-7", title: "Untamed", author: "Glennon Doyle", description: "A powerful memoir about discovering your true self and living boldly.", category: "Self-Discovery", amazon_link: "https://www.amazon.com/Untamed-Glennon-Doyle/dp/1984801252", icon: "Flame", recommended_by: "Crystal" },
  { id: "default-8", title: "Year of Yes", author: "Shonda Rhimes", description: "How to dance it out, stand in the sun, and be your own person.", category: "Growth", amazon_link: "https://www.amazon.com/Year-Yes-Dance-Stand-Person/dp/1476777128", icon: "Star", recommended_by: "Dr. Nicole" },
  { id: "default-11", title: "Women Who Run With the Wolves", author: "Clarissa Pinkola Estés", description: "Myths and stories of the wild woman archetype - reclaim your instinctual nature.", category: "Self-Discovery", amazon_link: "https://www.amazon.com/Women-Who-Run-Wolves-Archetype/dp/0345409876", icon: "Users", recommended_by: "Crystal & Dr. Nicole" },
  { id: "default-17", title: "The Mastery of Love", author: "Don Miguel Ruiz", description: "A practical guide to the art of relationship - a Toltec wisdom book.", category: "Relationships", amazon_link: "https://www.amazon.com/Mastery-Love-Practical-Relationship-Toltec/dp/1878424424", icon: "Heart", recommended_by: "Dr. Nicole" },
  
  // ===== CREATIVITY & PURPOSE =====
  { id: "default-9", title: "Big Magic", author: "Elizabeth Gilbert", description: "Creative living beyond fear - embrace curiosity and let go of needless suffering.", category: "Creativity", amazon_link: "https://www.amazon.com/Big-Magic-Creative-Living-Beyond/dp/1594634726", icon: "Sparkles", recommended_by: "Crystal" },
  { id: "default-10", title: "The Seat of the Soul", author: "Gary Zukav", description: "Align your personality with your soul and experience authentic power.", category: "Spirituality", amazon_link: "https://www.amazon.com/Seat-Soul-25th-Anniversary/dp/147676755X", icon: "Heart", recommended_by: "Dr. Nicole" },
  { id: "default-18", title: "The Alchemist", author: "Paulo Coelho", description: "A magical story about following your dreams and listening to your heart.", category: "Spirituality", amazon_link: "https://www.amazon.com/Alchemist-Paulo-Coelho/dp/0062315005", icon: "Sparkles", recommended_by: "Crystal & Dr. Nicole" },
  
  // ===== INSPIRING MEMOIRS =====
  { id: "default-19", title: "The Light We Carry", author: "Michelle Obama", description: "Overcoming in uncertain times - practical wisdom from the former First Lady.", category: "Resilience", amazon_link: "https://www.amazon.com/Light-We-Carry-Overcoming-Uncertain/dp/0593237463", icon: "Star", recommended_by: "Crystal & Dr. Nicole" },
  { id: "default-20", title: "More Than Enough", author: "Elaine Welteroth", description: "Claiming space for who you are, no matter what they say.", category: "Self-Worth", amazon_link: "https://www.amazon.com/More-Than-Enough-Claiming-Matter/dp/0525561587", icon: "Star", recommended_by: "Crystal" },
  { id: "default-21", title: "We're Going to Need More Wine", author: "Gabrielle Union", description: "Stories that are funny, sad, and true from one of Hollywood's most beloved actresses.", category: "Self-Discovery", amazon_link: "https://www.amazon.com/Were-Going-Need-More-Wine/dp/0062693980", icon: "Heart", recommended_by: "Dr. Nicole" },
  { id: "default-22", title: "Finding Me", author: "Viola Davis", description: "A deeply personal memoir about resilience, survival, and triumph.", category: "Resilience", amazon_link: "https://www.amazon.com/Finding-Me-Memoir-Viola-Davis/dp/0063037327", icon: "Flame", recommended_by: "Crystal & Dr. Nicole" },
  { id: "default-23", title: "Becoming", author: "Michelle Obama", description: "An intimate, powerful, and inspiring memoir by the former First Lady.", category: "Self-Discovery", amazon_link: "https://www.amazon.com/Becoming-Michelle-Obama/dp/1524763136", icon: "Star", recommended_by: "Crystal & Dr. Nicole" }
];

// Default downloadable resources
const DEFAULT_RESOURCES = [
  { id: "default-r1", title: "Little Black Book: Boundaries Edition", description: "Your essential guide to setting and maintaining healthy boundaries in all areas of life.", file_url: "https://customer-assets.emergentagent.com/job_3ad9932b-c6fe-42d6-ad4c-511f336327a7/artifacts/l0qsvdnt_Little%20Black%20Book%20Boundaries%20Edition%20.pdf", category: "Boundaries", icon: "Shield" },
  { id: "default-r2", title: "Mastering the Bounce Back", description: "A Businesswoman's Guide to Overcoming Setbacks - resilience strategies for professional women.", file_url: "https://customer-assets.emergentagent.com/job_3ad9932b-c6fe-42d6-ad4c-511f336327a7/artifacts/uohqph7a_Mastering%20the%20Bounce%20Back%20A%20Businesswoman%E2%80%99s%20Guide%20to%20Overcoming%20Setbacks%20%282%29.pdf", category: "Resilience", icon: "Flame" },
  { id: "default-r3", title: "What's Your Next Best Move", description: "A strategic e-book to help you identify and take your next powerful step forward.", file_url: "https://customer-assets.emergentagent.com/job_3ad9932b-c6fe-42d6-ad4c-511f336327a7/artifacts/fl5zkm0f_NEW%20What%27s%20Your%20Next%20Best%20Move%20E-Book%20V.3.pdf", category: "Strategy", icon: "Target" }
];

const LibraryPage = () => {
  const [books, setBooks] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchLibraryContent();
  }, []);

  const fetchLibraryContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [booksRes, resourcesRes] = await Promise.all([
        axios.get(`${API_URL}/api/library/books`, { headers }),
        axios.get(`${API_URL}/api/library/resources`, { headers })
      ]);
      
      // Use API data if available, otherwise use defaults
      setBooks(booksRes.data.books?.length > 0 ? booksRes.data.books : DEFAULT_BOOKS);
      setResources(resourcesRes.data.resources?.length > 0 ? resourcesRes.data.resources : DEFAULT_RESOURCES);
    } catch (error) {
      console.error('Failed to fetch library content:', error);
      // Fall back to defaults on error
      setBooks(DEFAULT_BOOKS);
      setResources(DEFAULT_RESOURCES);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || BookOpen;
  };
  
  // Get unique categories with "Our Books" first, then "all"
  const uniqueCategories = [...new Set(books.map(book => book.category))];
  const sortedCategories = [
    ...(uniqueCategories.includes('Our Books') ? ['Our Books'] : []),
    ...uniqueCategories.filter(c => c !== 'Our Books').sort()
  ];
  const categories = ['all', ...sortedCategories];
  
  const filteredBooks = selectedCategory === 'all' 
    ? books 
    : books.filter(book => book.category === selectedCategory);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="library-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading text-forest mb-2">Library</h1>
          <p className="text-gray-600">
            Curated resources and book recommendations from Crystal & Dr. Nicole to support your growth journey.
          </p>
        </div>

        <Tabs defaultValue="books" className="space-y-6">
          <TabsList className="bg-sand/50">
            <TabsTrigger value="books" className="data-[state=active]:bg-white">
              <BookOpen className="h-4 w-4 mr-2" />
              Book Recommendations
            </TabsTrigger>
            <TabsTrigger value="downloads" className="data-[state=active]:bg-white">
              <Download className="h-4 w-4 mr-2" />
              Downloadable Resources
            </TabsTrigger>
          </TabsList>

          {/* Book Recommendations Tab */}
          <TabsContent value="books" className="space-y-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? "bg-terracotta hover:bg-terracotta/90" : ""}
                >
                  {category === 'all' ? 'All Books' : category}
                </Button>
              ))}
            </div>

            {/* Books Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBooks.map(book => {
                const IconComponent = getIcon(book.icon);
                return (
                  <Card key={book.id} className="card-base hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                          <IconComponent className="h-5 w-5 text-terracotta" />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {book.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-heading text-forest mt-3">
                        {book.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-terracotta font-medium">
                        by {book.author}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">
                        {book.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          Recommended by {book.recommended_by}
                        </span>
                        <a 
                          href={book.amazon_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" variant="outline" className="text-forest hover:bg-forest hover:text-white">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Downloadable Resources Tab */}
          <TabsContent value="downloads" className="space-y-6">
            <Card className="card-base bg-gradient-to-r from-terracotta/5 to-transparent border-terracotta/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="h-5 w-5 text-terracotta" />
                  <h3 className="font-heading text-lg text-forest">Exclusive Resources</h3>
                </div>
                <p className="text-sm text-gray-600">
                  These downloadable guides and e-books are exclusive to Sister Diamonds members. 
                  Created by Crystal & Dr. Nicole to support your transformation journey.
                </p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.map(resource => {
                const IconComponent = getIcon(resource.icon);
                return (
                  <Card key={resource.id} className="card-base hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="h-12 w-12 rounded-xl bg-forest/10 flex items-center justify-center">
                          <IconComponent className="h-6 w-6 text-forest" />
                        </div>
                        <Badge className="bg-terracotta/10 text-terracotta border-0">
                          {resource.category || 'PDF'}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-heading text-forest mt-3">
                        {resource.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">
                        {resource.description}
                      </p>
                      <a 
                        href={resource.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        download
                      >
                        <Button className="w-full bg-forest hover:bg-forest/90 text-white">
                          <Download className="h-4 w-4 mr-2" />
                          Download Resource
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default LibraryPage;
